import type { ReactNode } from "react";
import Link from "next/link";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * 비로그인(게스트) 상태에서 "흐린 목업 + 가운데 로그인/회원가입 CTA" 패턴을 재사용하기 위한 공통 셸.
 *
 * `src/app/my/GuestDashboardPreview.tsx`(/my 인덱스 게스트 프리뷰)의 인라인 오버레이 구조를
 * 컴포넌트로 추출한 것(JCG — 학습 4개 탭 게스트 프리뷰 통일). 호출부는 흐리게 깔 목업 JSX 만
 * `children` 으로 넘기면 되고, blur 래퍼/그라데이션 오버레이/로그인 카드는 이 컴포넌트가 담당한다.
 *
 * - `children`(목업)은 가짜 데이터이므로 통째로 `aria-hidden` + `blur-[2px] pointer-events-none
 *   select-none` 으로 감싼다 → 스크린리더는 오버레이 카드만 읽는다.
 * - 색은 전부 디자인 토큰 + amber light/dark 페어(ui-patterns dark-mode-colors §2 준수).
 *   하드코딩 `bg-white`/`gray-900` 금지.
 * - 제목은 각 게스트 페이지의 단일 메인 제목이므로 `<h1>`. 장식 아이콘은 `aria-hidden`,
 *   회원가입 링크는 focus-visible 링 유지(a11y).
 * - blur 강도(`blur-[2px]`)·그라데이션(`from-background/50 via-background/70 to-background/95`)은
 *   레퍼런스(GuestDashboardPreview)와 동일 값을 유지한다.
 */
export default function GuestPreviewOverlay({
  title,
  description,
  children,
  className,
}: {
  /** 오버레이 카드의 메인 제목(페이지당 단일 h1). */
  title: string;
  /** 오버레이 카드의 보조 설명. */
  description: string;
  /** 흐리게 깔 목업 JSX(호출부는 목업만 넘긴다). */
  children: ReactNode;
  /** 바깥 relative 래퍼에 합칠 추가 클래스. */
  className?: string;
}) {
  return (
    <div className={cn("relative", className)}>
      {/* 흐린 목업 레이어 — 가짜 데이터이므로 통째로 접근성 트리에서 숨김 */}
      <div aria-hidden className="pointer-events-none select-none blur-[2px]">
        {children}
      </div>

      {/* 오버레이: 위→아래 그라데이션(토큰) + 가운데 로그인 안내 카드.
          뒤 목업이 비치도록 상·중단은 옅게, 하단만 짙게 덮는다. */}
      <div className="absolute inset-0 flex items-start justify-center bg-gradient-to-b from-background/50 via-background/70 to-background/95 pt-16 sm:pt-24">
        <div className="mx-4 w-full max-w-sm rounded-2xl border-2 border-border bg-card p-8 text-center shadow-xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300">
            <Sparkles className="h-7 w-7" aria-hidden />
          </div>
          <h1 className="mb-2 text-xl font-bold text-foreground">{title}</h1>
          <p className="mb-6 text-sm text-muted-foreground">{description}</p>
          <Button asChild size="lg" className="mx-auto w-full max-w-xs">
            <Link href="/sign-in">로그인하기</Link>
          </Button>
          <p className="mt-4 text-xs text-muted-foreground">
            아직 회원이 아니라면{" "}
            <Link
              href="/sign-up"
              className="rounded-sm font-semibold text-amber-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-400"
            >
              회원가입
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
